/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useEffect, useState } from 'react';
import Loading from '../../Shared/Loading';
import config from '../../Shared/config';
import { Stack } from '@mui/material';
import {
  WorkflowEngineActionKind,
  WorkflowEngineContext
} from '../../WorkflowEngine/workflowEngineReducer';
import { WorkflowLockStatus } from '../../WorkflowEngine/types';
import { get, put } from '../../Shared/ajax';
import { useSnackbar } from 'notistack';
import { AppContext } from '../../App/appReducer';
import { AppActionKind, AppPageKind } from '../../App/types';

declare global {
  interface Window {
    GrapeCity: any;
    __isReactDndBackendSetUp: boolean;
    __removeEventListeners: (eventType: string) => string[];
  }
}

export default ({
  versionExternalId,
  isReadOnly
}: {
  versionExternalId: string;
  isReadOnly: boolean;
}) => {
  const reportId = 'designer-host';

  const { state, dispatch: workflowDispatch } = useContext(
    WorkflowEngineContext
  );

  const { dispatch: appDispatch } = useContext(AppContext);

  const { externalId } = state;

  const [loading, setLoading] = useState<boolean>(true);

  const { enqueueSnackbar } = useSnackbar();

  const handleOnBeforeSaveAsync = async () => {
    try {
      const url = `DynamicWorkflow/${externalId}/LockStatus`;
      const response = await get<WorkflowLockStatus>(url);

      if (response?.isLockedByOtherUser) {
        enqueueSnackbar(
          `This workflow has been locked for editing by ${response?.lockedBy}`,
          { variant: 'error' }
        );
        appDispatch({
          type: AppActionKind.SET_APPPAGE,
          payload: AppPageKind.LANDING
        });
        return false;
      }

      return true;

      // eslint-disable-next-line no-empty
    } catch (error) {
      enqueueSnackbar((error as Error).message, { variant: 'error' });
      return false;
    }
  };

  const handleOnAfterSaveAsync = async () => {
    try {
      const url = `DynamicWorkflow/${externalId}/Lock`;
      const response = await put<WorkflowLockStatus, unknown>(url, {});

      if (!response?.isLockedByOtherUser) {
        enqueueSnackbar('Configuration successfully saved!', {
          variant: 'success'
        });
        workflowDispatch({
          type: WorkflowEngineActionKind.SET_WORKFLOW_LOCK_STATUS,
          payload: response
        });
      }

      // eslint-disable-next-line no-empty
    } catch (error) {
      enqueueSnackbar((error as Error).message, { variant: 'error' });
    }
  };

  const initReport = (id: string) => {
    const options =
      window.GrapeCity.ActiveReports.WebDesigner.createDesignerOptions();
    options.reportInfo = { id: id, name: id };
    options.reportItemsFeatures.table.autoFillFooter = true;
    options.openButton.visible = false;
    options.saveButton.visible = true;
    options.saveAsButton.visible = false;
    options.documentation.home =
      'https://www.grapecity.com/activereportsnet/docs/latest/online-webdesigner/overview.html';
    options.dataTab.dataSets.canModify = true;
    options.dataTab.dataSources.canModify = false;
    options.restoreUnsavedReport = false;
    options.fileView.visible = false;
    options.server.url = config.proxyApiUrl + '/api/DynamicWorkflowReport';
    options.saveButton.visible = !isReadOnly;
    options.server.onBeforeRequest = function (init: any) {
      init.headers = {
        ExternalId: id,
        ...init.headers
      };
    };

    // Ref. https://www.grapecity.com/blogs/whats-new-activereports-v15-2
    options.documentApiHandlers.onBeforeSave = handleOnBeforeSaveAsync;
    options.documentApiHandlers.onAfterSave = handleOnAfterSaveAsync;

    options.openViewer = openViewer.bind(options);

    window.GrapeCity.ActiveReports.WebDesigner.renderApplication(
      reportId,
      options
    );
  };

  let jsViewer: any;

  const openViewer = (options: any) => {
    if (jsViewer) {
      jsViewer.openReport(options.reportInfo.id);
      return;
    }

    jsViewer = window.GrapeCity.ActiveReports.JSViewer.create({
      locale: options.locale,
      element: '#' + options.element,
      reportService: {
        url: config.proxyApiUrl + '/api/DynamicWorkflowReport/reporting',
        onRequest: (init: any) => delete init.credentials
      },
      reportID: options.reportInfo.id,
      settings: {
        zoomType: 'FitPage'
      }
    });
  };

  const getLinkEl = (id: string, href: string) => {
    const linkEl = document.createElement('link');
    linkEl.id = id;
    linkEl.href = href;
    linkEl.rel = 'stylesheet';
    linkEl.type = 'text/css';
    return linkEl;
  };

  const getScriptEl = (id: string, src: string, callback?: () => void) => {
    const scriptEl = document.createElement('script');
    scriptEl.id = id;
    scriptEl.src = src;
    scriptEl.type = 'text/javascript';
    if (callback) scriptEl.onload = callback;
    return scriptEl;
  };

  const removeLinkEl = (id: string) => {
    const refEl = document.getElementById(id);
    if (refEl) document.head.removeChild(refEl);
  };

  const removeScriptEl = (id: string) => {
    const refEl = document.getElementById(id);
    if (refEl) document.body.removeChild(refEl);
  };

  const loadExternalAssets = () => {
    const serverBasePath = config.assetBaseUrl;

    document.head?.appendChild(
      getLinkEl(
        'fontGoogleApisCss',
        `${serverBasePath}/assets/vendor/css/fonts-googleapis.css`
      )
    );
    document.head?.appendChild(
      getLinkEl('jsViewerCss', `${serverBasePath}/assets/jsViewer.min.css`)
    );
    document.head?.appendChild(
      getLinkEl('webDesignerCss', `${serverBasePath}/assets/web-designer.css`)
    );

    document.body?.appendChild(
      getScriptEl('jsViewerJs', `${serverBasePath}/assets/jsViewer.min.js`)
    );
    document.body?.appendChild(
      getScriptEl(
        'webDesignerJs',
        `${serverBasePath}/assets/web-designer.js`,
        () => {
          initReport(versionExternalId);
          setLoading(false);
        }
      )
    );
  };

  const removeExternalAssets = () => {
    removeLinkEl('fontGoogleApisCss');
    removeLinkEl('jsViewerCss');
    removeLinkEl('webDesignerCss');
    removeScriptEl('jsViewerJs');
    removeScriptEl('webDesignerJs');
  };

  useEffect(() => {
    loadExternalAssets();

    return () => {
      removeExternalAssets();
    };
  }, []);

  return loading ? (
    <Loading />
  ) : (
    <Stack
      id={reportId}
      sx={{
        margin: '0 auto',
        width: '100%',
        height: 'calc(100vh - 64px)'
      }}
    ></Stack>
  );
};
